/**
 * Reset Password Page
 * Handles password reset token from URL.
 * Shows a form to set a new password, then redirects to login.
 */

import { VALIDATION_LIMITS } from '@config/constants';
import { useAuth } from '@contexts/AuthContext';
import { Check, Eye, EyeOff, KeyRound, Loader2, X } from 'lucide-react';
import { type FormEvent, type ReactElement, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

type ResetStatus = 'form' | 'submitting' | 'success' | 'error';

export function ResetPasswordPage(): ReactElement {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState<ResetStatus>(token ? 'form' : 'error');
  const [error, setError] = useState<string | null>(token ? null : 'No reset token provided');

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (password.length < VALIDATION_LIMITS.PASSWORD_MIN_LENGTH) {
      setError(`Password must be at least ${VALIDATION_LIMITS.PASSWORD_MIN_LENGTH} characters`);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!token) {
      setError('No reset token provided');
      return;
    }

    setStatus('submitting');

    try {
      await resetPassword(token, password);
      setStatus('success');
    } catch (err) {
      setStatus('form');
      setError((err as Error).message);
    }
  };

  const handleGoToLogin = (): void => {
    navigate('/login');
  };

  return (
    <div className="min-h-dvh bg-theme-bg flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-theme-surface rounded-xl border border-theme-border p-8 text-center">
        {status === 'form' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-theme-primary/15 flex items-center justify-center">
              <KeyRound className="w-8 h-8 text-theme-primary" />
            </div>
            <h1 className="text-2xl font-bold text-theme-text mb-2">Set New Password</h1>
            <p className="text-theme-text-secondary mb-6">Enter your new password below.</p>

            <form onSubmit={handleSubmit} className="space-y-4 text-left">
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-theme-text-secondary mb-1"
                >
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-theme-bg border border-theme-border rounded-lg px-4 py-3 pr-12 text-theme-text placeholder:text-theme-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-theme-primary/50 focus:border-theme-primary"
                    placeholder="Enter new password"
                    minLength={VALIDATION_LIMITS.PASSWORD_MIN_LENGTH}
                    maxLength={VALIDATION_LIMITS.PASSWORD_MAX_LENGTH}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-theme-text-secondary hover:text-theme-text"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-theme-text-secondary mb-1"
                >
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-theme-bg border border-theme-border rounded-lg px-4 py-3 text-theme-text placeholder:text-theme-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-theme-primary/50 focus:border-theme-primary"
                  placeholder="Confirm new password"
                  minLength={VALIDATION_LIMITS.PASSWORD_MIN_LENGTH}
                  maxLength={VALIDATION_LIMITS.PASSWORD_MAX_LENGTH}
                  required
                />
              </div>

              {error && <p className="text-theme-danger text-sm">{error}</p>}

              <button
                type="submit"
                className="w-full bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill font-medium py-3 px-4 rounded-lg transition-colors"
              >
                Reset Password
              </button>
            </form>

            <button
              type="button"
              onClick={handleGoToLogin}
              className="mt-4 text-sm text-theme-text-secondary hover:text-theme-text transition-colors"
            >
              Back to Login
            </button>
          </>
        )}

        {status === 'submitting' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-theme-primary/15 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-theme-primary animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-theme-text mb-2">Resetting Password</h1>
            <p className="text-theme-text-secondary">Please wait...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-theme-success/15 flex items-center justify-center">
              <Check className="w-8 h-8 text-theme-success" />
            </div>
            <h1 className="text-2xl font-bold text-theme-text mb-2">Password Reset</h1>
            <p className="text-theme-text-secondary mb-6">
              Your password has been updated. You can now log in with your new password.
            </p>
            <button
              type="button"
              onClick={handleGoToLogin}
              className="w-full bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill font-medium py-3 px-4 rounded-lg transition-colors"
            >
              Go to Login
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-theme-danger/15 flex items-center justify-center">
              <X className="w-8 h-8 text-theme-danger" />
            </div>
            <h1 className="text-2xl font-bold text-theme-text mb-2">Reset Failed</h1>
            <p className="text-theme-text-secondary mb-6">
              {error || 'The reset link is invalid or has expired.'}
            </p>
            <button
              type="button"
              onClick={handleGoToLogin}
              className="w-full bg-theme-surface-hover hover:bg-theme-border text-theme-text font-medium py-3 px-4 rounded-lg transition-colors"
            >
              Go to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
